/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { DriverErrorTypes, } from "@fluidframework/driver-definitions/internal";
import { LoggingError } from "@fluidframework/telemetry-utils/internal";
/**
 * @internal
 */
export var OnlineStatus;
(function (OnlineStatus) {
    OnlineStatus[OnlineStatus["Offline"] = 0] = "Offline";
    OnlineStatus[OnlineStatus["Online"] = 1] = "Online";
    OnlineStatus[OnlineStatus["Unknown"] = 2] = "Unknown";
})(OnlineStatus || (OnlineStatus = {}));
/**
 * It tells if we have local connection only - we might not have connection to web.
 * No solution for node.js (other than resolve dns names / ping specific sites)
 * Can also use window.addEventListener("online" / "offline")
 * @internal
 */
export function isOnline() {
    if (typeof navigator === "object" &&
        navigator !== null &&
        typeof navigator.onLine === "boolean") {
        return navigator.onLine ? OnlineStatus.Online : OnlineStatus.Offline;
    }
    return OnlineStatus.Unknown;
}
/**
 * Generic network error class.
 * @internal
 */
export class GenericNetworkError extends LoggingError {
    constructor(message, canRetry, props) {
        super(message, props);
        this.canRetry = canRetry;
        /**
         * {@inheritDoc @fluidframework/telemetry-utils#IFluidErrorBase.errorType}
         */
        this.errorType = DriverErrorTypes.genericNetworkError;
    }
}
/**
 * FluidInvalidSchema error class.
 * @internal
 */
export class FluidInvalidSchemaError extends LoggingError {
    constructor(message, props) {
        super(message, props);
        this.errorType = DriverErrorTypes.fluidInvalidSchema;
        this.canRetry = false;
    }
}
/**
 * @internal
 */
export class DeltaStreamConnectionForbiddenError extends LoggingError {
    constructor(message, props, storageOnlyReason) {
        super(message, { ...props, statusCode: 400 });
        this.errorType = DeltaStreamConnectionForbiddenError.errorType;
        this.canRetry = false;
        this.storageOnlyReason = storageOnlyReason;
    }
}
DeltaStreamConnectionForbiddenError.errorType = DriverErrorTypes.deltaStreamConnectionForbidden;
/**
 * @internal
 */
export class AuthorizationError extends LoggingError {
    constructor(message, claims, tenantId, props) {
        // don't log claims or tenantId
        super(message, props, new Set(["claims", "tenantId"]));
        this.claims = claims;
        this.tenantId = tenantId;
        this.errorType = DriverErrorTypes.authorizationError;
        this.canRetry = false;
    }
}
/**
 * @internal
 */
export class LocationRedirectionError extends LoggingError {
    constructor(message, redirectUrl, props) {
        // do not log redirectURL
        super(message, props, new Set(["redirectUrl"]));
        this.redirectUrl = redirectUrl;
        this.errorType = DriverErrorTypes.locationRedirection;
        this.canRetry = false;
    }
}
/**
 * @internal
 */
export class NetworkErrorBasic extends LoggingError {
    constructor(message, errorType, canRetry, props) {
        super(message, props);
        this.errorType = errorType;
        this.canRetry = canRetry;
    }
}
/**
 * @internal
 */
export class NonRetryableError extends NetworkErrorBasic {
    constructor(message, errorType, props) {
        super(message, errorType, false, props);
        this.errorType = errorType;
    }
}
/**
 * @internal
 */
export class RetryableError extends NetworkErrorBasic {
    constructor(message, errorType, props) {
        super(message, errorType, true, props);
        this.errorType = errorType;
    }
}
/**
 * Throttling error class - used to communicate all throttling errors
 * @internal
 */
export class ThrottlingError extends LoggingError {
    constructor(message, retryAfterSeconds, props) {
        super(message, props);
        this.retryAfterSeconds = retryAfterSeconds;
        this.errorType = DriverErrorTypes.throttlingError;
        this.canRetry = true;
    }
}
/**
 * @internal
 */
export const createWriteError = (message, props) => new NonRetryableError(message, DriverErrorTypes.writeError, props);
/**
 * @internal
 */
export function createGenericNetworkError(message, retryInfo, props) {
    if (retryInfo.retryAfterMs !== undefined && retryInfo.canRetry) {
        return new ThrottlingError(message, retryInfo.retryAfterMs / 1000, props);
    }
    return new GenericNetworkError(message, retryInfo.canRetry, props);
}
/**
 * Check if a connection error can be retried.  Unless explicitly allowed, retry is disallowed.
 * I.e. asserts or unexpected exceptions in our code result in container failure.
 * @param error - The error to inspect for ability to retry
 * @internal
 */
export const canRetryOnError = (error) => error?.canRetry === true;
/**
 * Check retryAfterSeconds property on error
 * @internal
 */
export const getRetryDelaySecondsFromError = (error) => error?.retryAfterSeconds;
/**
 * Check retryAfterSeconds property on error and convert to ms
 * @internal
 */
export const getRetryDelayFromError = (error) => error?.retryAfterSeconds !== undefined ? error.retryAfterSeconds * 1000 : undefined;
//# sourceMappingURL=network.js.map